import {HttpHeaders} from '@angular/common/http';

/**
 * The summary line above a Quotes / Orders / Sample Requests listing — the ONE place
 * the listing's `X-Summary-…` response headers become view state.
 *
 * Nine screens carry such a line: the three listings, plus the three shared tab
 * components (`app-account-quotes`, `app-account-orders`,
 * `app-account-sample-requests`), each rendered once for an account and once for a
 * contact. They used to compute their own figures from their own extra fetch, which
 * is how they drifted:
 *
 *  - the totals ignored the toolbar entirely, so the line described the whole book
 *    while the table under it showed a filtered slice;
 *  - "expiring within 7 days" was structurally always 0, because those fetches asked
 *    for a narrowed `fields` list that left out `type` — and the API's `daysToExpiry`
 *    expand is restricted to `type = Q`, so it was skipped and came back undefined;
 *  - the Sample Requests lines came from `quote/counter`, whose contract accepts only
 *    an account/contact and a type, so it could not see a date range at all;
 *  - the same metric was defined slightly differently in each component.
 *
 * Now each screen sets `summary = true` on the ONE list request it already makes, and
 * the API answers over that request's whole filtered set with database aggregates,
 * on the same response. One filter state → one filtered dataset → one authoritative
 * summary, with no second request that could describe something else.
 *
 * Date ranges are interpreted in the business timezone (Australia/Sydney) server-side,
 * by the same `created_at` coercion the rows go through — see `rangeParam` in the
 * listing components for the wire format that triggers it.
 */
export interface ListingSummary {
  /** Rows matching the active filters — the same number the pager reports. */
  totalCount: number;
  /** Quotes: still-open quotes. Orders: orders in progress. */
  openCount: number;
  /** Σ final total (inc. GST) across `openCount`'s rows. */
  openValue: number;
  /** Quotes only: of the open ones, how many fall due inside `expiringSoonInDays`. */
  expiringSoon: number;
  /** The window `expiringSoon` was counted over — the server owns the number. */
  expiringSoonInDays: number;
  /** Orders only: orders sitting on Final Payment Outstanding. */
  finalPaymentOutstanding: number;
  /** Sample requests: Σ final total across the whole filtered set. */
  totalValue: number;
  /** Sample requests: Σ line-item quantities across the whole filtered set. */
  totalPieces: number;
}

/** How many days ahead "expiring soon" looks, until the server says otherwise. */
export const EXPIRING_SOON_IN_DAYS = 7;

/**
 * A listing response's `X-Summary-…` headers → the view model above.
 *
 * Returns null when the response carries no summary at all (the request did not ask
 * for one, or the aggregate failed server-side) — call sites leave their previous
 * "Loading summary…" state up rather than render a misleading 0.
 *
 * Metrics that don't apply to the module have no header, and land as 0 here — which
 * is also what an empty filter result reads as, so no `undefined`, `null` or `NaN`
 * can reach the line.
 */
export function toListingSummary(headers: HttpHeaders | null | undefined): ListingSummary | null {
  if (!headers || !headers.has('X-Summary-Total-Count')) {
    return null;
  }
  const num = (name: string) => +(headers.get(name) ?? 0) || 0;
  return {
    totalCount: num('X-Summary-Total-Count'),
    openCount: num('X-Summary-Open-Count'),
    openValue: num('X-Summary-Open-Value'),
    expiringSoon: num('X-Summary-Expiring-Soon-Count'),
    expiringSoonInDays: num('X-Summary-Expiring-Soon-Days') || EXPIRING_SOON_IN_DAYS,
    finalPaymentOutstanding: num('X-Summary-Final-Payment-Outstanding-Count'),
    totalValue: num('X-Summary-Total-Value'),
    totalPieces: num('X-Summary-Total-Pieces')
  };
}
